import { styled } from "goober";
import { useState } from "react";
import { Icon } from "../Icon";
import { Text } from "../Text";
import { Binary } from "./Binary";
import { DownloadButton } from "./DownloadButton";
import { extensionFromMime } from "./download";
import type { FileRenderControl } from "./types";
import { useFileActions } from "./useFileActions";
import { useObjectUrl } from "./useObjectUrl";

const Container = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  height: 100%;
  min-width: 0;
  min-height: 0;
`;

const Preview = styled("img")`
  display: block;
  flex: 1;
  min-height: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
  border-radius: 8px;
`;

type Props = {
  contentType: string;
  url?: string;
  fileName?: string;
  onControls?: (controls: FileRenderControl[]) => void;
};

export function Image({ contentType, url, fileName, onControls }: Props) {
  const { blobUrl, failed } = useObjectUrl(url);
  const [undecodableUrl, setUndecodableUrl] = useState<string>();
  const downloadName = fileName ?? `image.${extensionFromMime(contentType)}`;
  const { download, downloading } = useFileActions({
    url,
    fileName: downloadName,
    onControls,
  });

  if (failed || (blobUrl && undecodableUrl === blobUrl))
    return (
      <Binary
        url={url}
        contentType={contentType}
        fileName={fileName}
        hideActions={!!onControls}
        note="Couldn’t display this image"
      />
    );

  if (!blobUrl) return null;

  return (
    <Container>
      <Preview
        src={blobUrl}
        alt={downloadName}
        onError={() => setUndecodableUrl(blobUrl)}
      />
      {!onControls && (
        <div style={{ marginLeft: "auto" }}>
          <DownloadButton onPress={download} disabled={!url || downloading}>
            <Icon name="arrow-down" size={12} />
            <Text variant="text-small-md">
              {downloading ? "Downloading…" : "Download"}
            </Text>
          </DownloadButton>
        </div>
      )}
    </Container>
  );
}
